import type { CabinetAppointmentView } from "@planerius/contracts/cabinet"

import type { PermissionSet } from "../../access/permissions"
import { hasPermission } from "../../access/permissions"

/**
 * ЧТО ВИДНО МАСТЕРУ: телефон клиента и деньги (настройки владельца 4-Б12).
 *
 * ГЛАВНОЕ РЕШЕНИЕ ЭТОГО ФАЙЛА — ВИДИМОСТЬ ЧИТАЕТСЯ ИЗ ДАННЫХ, А НЕ СЧИТАЕТСЯ ЗАНОВО.
 *
 * Обе настройки живут на сервере. Деньги гасятся в самой выборке: при выключенных
 * приходит priceMinor = null у записи и у каждой её услуги. Телефон мастеру не приходит
 * с днём никогда — ни при какой настройке: сервер говорит, КАК его можно открыть
 * (client.phoneAccess), и номер уходит в браузер только действием, со строкой в журнале
 * (apps/api/src/common/staff-visibility.ts, решения владельца 45 и 46).
 *
 * Поэтому кабинету достаточно читать ответ сервера. Второй способ
 * («роль мастер, настройка выключена, значит прячем») хуже по двум причинам сразу:
 * во-первых, он повторяет серверное правило второй раз и однажды разойдётся с ним;
 * во-вторых, если бы сервер поле всё-таки прислал, оно уже лежало бы в браузере —
 * и «спрятано» означало бы только «спрятано от глаз честного человека».
 *
 * ОТСЮДА ЖЕ «одна настройка действует на все экраны разом»: все четыре экрана мастера
 * читают один и тот же ответ журнала, и решение принято ровно один раз — на сервере.
 * Рассогласоваться между «Моим днём» и карточкой записи им нечем.
 *
 * ПУСТОЙ ДЕНЬ. На дне БЕЗ записей узнать настройку денег не из чего: гасить в пустом
 * дне нечего, а GET /v1/cabinet/settings/staff-visibility закрыт правом settings.view,
 * которого у мастера нет. Поэтому пустой день считается «деньги скрыты» — в сторону
 * НЕПОКАЗА: лишний раз не показать цену дешевле, чем показать её тому, кому салон
 * её не открывал.
 */

export interface MasterVisibility {
  /** Цена услуги, итог смены, суммы в форме записи. */
  readonly money: boolean
  /**
   * Есть ли в этом дне номера, которые мастеру вообще можно открыть. Как именно —
   * у каждой записи свой ответ (phoneAccessOf): сервер решает это по записи, потому что
   * «свой клиент» — свойство записи, а не дня.
   */
  readonly phone: boolean
}

/** Всё скрыто. Точка отсчёта — та же, что у сервера: умолчание в сторону непоказа. */
export const HIDDEN_VISIBILITY: MasterVisibility = { money: false, phone: false }

/**
 * Как мастеру доступен номер клиента этой записи — ответ сервера (AppointmentClientBrief).
 *
 *   shown      — номер уже пришёл в поле phone (право clients.phone.view);
 *   on_open    — салон включил «мастер видит телефон»: номер открывается сам при
 *                открытии карточки и кнопкой звонка в ленте;
 *   on_request — умолчание салона: маска и «Показать»;
 *   none       — номер не открывается.
 *
 * РАНЬШЕ ЭКРАН РЕШАЛ ЭТО САМ — по праву clients.phone.view, и у мастера кнопка
 * «Показать» была мёртвой всегда: маршрут открытия был закрыт этим правом. По ТЗ
 * именно она и есть умолчание (05, §5.1: «маска и действие „Показать“; открытие
 * пишется в журнал»). Теперь решает сервер, а экран исполняет — второго источника
 * правды нет. В обоих рабочих положениях номер открывается запросом, и каждое
 * открытие остаётся в журнале действий салона (04, Б12).
 */
export type PhoneAccess = CabinetAppointmentView["client"]["phoneAccess"]

export function phoneAccessOf(appointment: CabinetAppointmentView): PhoneAccess {
  // Ответ старого сервера без поля — «не открывается»: ошибка в сторону непоказа.
  return appointment.client.phoneAccess ?? "none"
}

/**
 * Что показывать этому человеку в его дне.
 *
 * `appointments` — записи ЕГО колонки: настройка открывает мастеру свои строки, а не
 * весь филиал, и судить по чужой строке о своей видимости нельзя.
 */
export function resolveMasterVisibility(input: {
  readonly permissions: PermissionSet
  readonly appointments: readonly CabinetAppointmentView[]
}): MasterVisibility {
  // Право сильнее настройки: у владельца и бухгалтера деньги видны всегда, и пустой день
  // не должен прятать от них то, что им открыто по роли.
  const money =
    hasPermission(input.permissions, "finance.view") ||
    input.appointments.some((appointment) => appointment.priceMinor !== null)

  const phone =
    hasPermission(input.permissions, "clients.phone.view") ||
    input.appointments.some((appointment) => phoneAccessOf(appointment) !== "none")

  return { money, phone }
}

/**
 * Сумма, которую видно. null — деньги скрыты ИЛИ считать нечего; это одно и то же
 * состояние экрана: строки итога нет вовсе (§5.2, «выключенное — отсутствие блока»).
 */
export function visibleTotalMinor(
  appointments: readonly CabinetAppointmentView[],
): number | null {
  const priced = appointments.filter(
    (appointment): appointment is CabinetAppointmentView & { priceMinor: number } =>
      appointment.priceMinor !== null,
  )
  if (priced.length === 0) return null
  return priced.reduce((sum, appointment) => sum + appointment.priceMinor, 0)
}
